import {
  Mark,
  type Editor,
  type JSONContent,
  type MarkdownToken,
} from "@tiptap/core";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

/** The attribute that marks commented text, in the editor and in saved markdown. */
export const COMMENT_ATTRIBUTE = "data-ht-comment";

const COMMENT_TOKEN = "htComment";

type JSONMark = NonNullable<JSONContent["marks"]>[number];
const OPEN_TAG = /^<span data-ht-comment="([^"]+)">/;

/** Styles for highlighted comments, to merge into the editor's own. */
export const COMMENT_HIGHLIGHT_SX = {
  ".ht-comment-highlight": {
    backgroundColor: "warning.backgroundHover",
    borderBottom: "2px solid",
    borderColor: "warning.border",
    cursor: "pointer",
  },
  ".ht-comment-highlight-active": { backgroundColor: "warning.200" },
};

/** The ids of every comment marked in a document. */
export function markedCommentIds(doc: ProseMirrorNode): Set<string> {
  const ids = new Set<string>();
  doc.descendants((node) => {
    for (const mark of node.marks) {
      const id: unknown = mark.attrs.threadId;
      if (mark.type.name === "comment" && typeof id === "string") ids.add(id);
    }
  });
  return ids;
}

/**
 * Highlights the comments whose threads are open; a resolved or deleted
 * thread's marker stays, unhighlighted. `draft` is the text a comment is being
 * written on, kept highlighted while the comment box has focus.
 */
export function setCommentHighlights(
  editor: Editor,
  highlights: HighlightState,
) {
  if (editor.isDestroyed) return;
  editor.view.dispatch(
    editor.state.tr
      .setMeta(highlightsKey, highlights)
      .setMeta("addToHistory", false),
  );
}

/** The draft's text as it stands now, moved along by any edits since it was picked. */
export function commentDraftRange(editor: Editor): HighlightState["draft"] {
  return highlightsKey.getState(editor.state)?.draft ?? null;
}

export type HighlightState = {
  openThreadIds: ReadonlySet<string>;
  activeThreadId: string | null;
  draft: { from: number; to: number } | null;
};

type PluginState = HighlightState & { decorations: DecorationSet };

const highlightsKey = new PluginKey<PluginState>("commentHighlights");

const EMPTY_HIGHLIGHTS: HighlightState = {
  openThreadIds: new Set(),
  activeThreadId: null,
  draft: null,
};

function withDecorations(
  highlights: HighlightState,
  doc: ProseMirrorNode,
): PluginState {
  const decorations: Decoration[] = highlights.draft
    ? [
        Decoration.inline(highlights.draft.from, highlights.draft.to, {
          class: "ht-comment-highlight ht-comment-highlight-active",
        }),
      ]
    : [];
  if (highlights.openThreadIds.size > 0) {
    doc.descendants((node, pos) => {
      if (!node.isText) return;
      for (const mark of node.marks) {
        const id: unknown = mark.attrs.threadId;
        if (mark.type.name !== "comment" || typeof id !== "string") continue;
        if (!highlights.openThreadIds.has(id)) continue;
        decorations.push(
          Decoration.inline(pos, pos + node.nodeSize, {
            class:
              id === highlights.activeThreadId
                ? "ht-comment-highlight ht-comment-highlight-active"
                : "ht-comment-highlight",
          }),
        );
      }
    });
  }
  return { ...highlights, decorations: DecorationSet.create(doc, decorations) };
}

/**
 * A comment on a passage of text, kept in the document itself as
 * `<span data-ht-comment="threadId">` so edits, from people or agents, carry
 * it along with its text. The thread lives in `asset_comment_threads`.
 */
export const CommentMark = Mark.create({
  name: "comment",
  // One comment per stretch of text: the markdown can't keep two of the same mark on it. A nested
  // span hands its text to the inner comment, so both threads keep a marker through a save.
  // Typing at a comment's edge doesn't extend it.
  inclusive: false,

  addAttributes() {
    return {
      threadId: {
        default: null,
        parseHTML: (element) => element.getAttribute(COMMENT_ATTRIBUTE),
        renderHTML: (attributes) => ({
          [COMMENT_ATTRIBUTE]: attributes.threadId,
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: `span[${COMMENT_ATTRIBUTE}]` }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["span", HTMLAttributes, 0];
  },

  markdownTokenName: COMMENT_TOKEN,

  // Its own tokenizer rather than the markdown parser's inline-HTML handling,
  // which reads the span's content as HTML and saves `**May**` back escaped.
  markdownTokenizer: {
    name: COMMENT_TOKEN,
    level: "inline",
    start: (src: string) => src.indexOf(`<span ${COMMENT_ATTRIBUTE}=`),
    tokenize(src: string, _tokens, lexer) {
      const open = OPEN_TAG.exec(src);
      if (!open) return undefined;
      const end = closingTagIndex(src, open[0].length);
      if (end < 0) return undefined;
      const inner = src.slice(open[0].length, end);
      return {
        type: COMMENT_TOKEN,
        raw: src.slice(0, end + "</span>".length),
        threadId: open[1],
        tokens: lexer.inlineTokens(inner),
      };
    },
  },

  parseMarkdown(token: MarkdownToken, helpers) {
    return withComment(helpers.parseInline(token.tokens ?? []), {
      type: "comment",
      attrs: { threadId: token.threadId },
    });
  },

  renderMarkdown(node, helpers) {
    const threadId: unknown = node.attrs?.threadId;
    const text = helpers.renderChildren(node);
    return typeof threadId === "string"
      ? `<span ${COMMENT_ATTRIBUTE}="${threadId}">${text}</span>`
      : text;
  },

  addProseMirrorPlugins() {
    return [
      new Plugin<PluginState>({
        key: highlightsKey,
        state: {
          init: (_config, state) =>
            withDecorations(EMPTY_HIGHLIGHTS, state.doc),
          // Rebuilt only when the doc or what's highlighted changes, not on every selection move.
          apply: (tr, value) => {
            const next: HighlightState | undefined = tr.getMeta(highlightsKey);
            if (next) return withDecorations(next, tr.doc);
            if (!tr.docChanged) return value;
            // The draft's text moves with edits made while its comment is written.
            const draft = value.draft && {
              from: tr.mapping.map(value.draft.from),
              to: tr.mapping.map(value.draft.to),
            };
            return withDecorations(
              {
                ...value,
                draft: draft && draft.from < draft.to ? draft : null,
              },
              tr.doc,
            );
          },
        },
        props: {
          decorations: (state) => highlightsKey.getState(state)?.decorations,
        },
      }),
    ];
  },
});

/** Marks the text not already in a nested comment, which keeps its own thread. */
function withComment(content: JSONContent[], mark: JSONMark): JSONContent[] {
  return content.map((node) => {
    if (node.type !== "text")
      return node.content
        ? { ...node, content: withComment(node.content, mark) }
        : node;
    const marks = node.marks ?? [];
    return marks.some(({ type }) => type === "comment")
      ? node
      : { ...node, marks: [...marks, mark] };
  });
}

/** Where the `</span>` closing the span opened just before `from` is, counting nested spans. */
function closingTagIndex(src: string, from: number): number {
  let depth = 1;
  const tags = /<span\b[^>]*>|<\/span>/g;
  tags.lastIndex = from;
  for (let match = tags.exec(src); match; match = tags.exec(src)) {
    depth += match[0] === "</span>" ? -1 : 1;
    if (depth === 0) return match.index;
  }
  return -1;
}
